/*
 * Stick-to-bottom pin logic, kept pure so it can be unit-tested without a
 * layout engine (jsdom has none). The chat follows the newest text only while
 * the user is at the bottom; an upward gesture stops the follow; returning to
 * the bottom resumes it.
 *
 * WHY GESTURES, NOT SCROLL DELTAS. The first version unpinned whenever scrollY
 * dropped a couple of pixels between scroll events. The browser generates
 * exactly that without any user involvement: when a streaming re-render
 * transiently shrinks the document, scrollY is clamped down; when scroll
 * anchoring adjusts around a smooth animation, scrollY moves down. Every such
 * adjustment read as "the user scrolled up", the follow stopped mid-stream and
 * the jump pill appeared over an answer the user never left. So intent is now
 * taken from the only signals a browser cannot emit on its own: a wheel tick,
 * a touch drag, a navigation key. Scroll position is used solely to RE-pin.
 */

// How close to the bottom (px) still counts as "pinned".
export const NEAR_BOTTOM_PX = 100;

// A finger has to travel this far before it reads as a deliberate drag rather
// than the jitter of a resting touch.
export const TOUCH_SLOP_PX = 8;

/** Within the near-bottom band, where the follow (re)engages. */
export function isNearBottom({ scrollY, scrollHeight, innerHeight }) {
  return scrollHeight - innerHeight - scrollY <= NEAR_BOTTOM_PX;
}

/** A wheel tick with negative deltaY is the user reaching upward. */
export function wheelUnpins(deltaY) {
  return deltaY < 0;
}

/**
 * A touch dragging DOWN the screen scrolls the content up. startY/currentY are
 * clientY values from touchstart and the latest touchmove.
 */
export function touchUnpins(startY, currentY) {
  return currentY - startY > TOUCH_SLOP_PX;
}

/**
 * Keys that read something above the fold. Space and End scroll down; they
 * never unpin. The caller is responsible for ignoring keys typed into inputs,
 * where ArrowUp moves a caret, not the page.
 */
export function keyUnpins(key) {
  return key === 'ArrowUp' || key === 'PageUp' || key === 'Home';
}

/*
 * RE-PINNING, tightened once the follow started landing on the true bottom.
 *
 * The follow used to stop short of the bottom by the composer's reserved
 * padding, so every resting view sat 260px from the end and a small upward
 * scroll never came near the 100px band. Now that the follow reaches the end,
 * "any scroll event within 100px re-pins" would snap a deliberate 60px
 * scroll-up straight back on the next streamed chunk: exactly the override the
 * user must never see. So a scroll event alone re-pins only when the view is
 * genuinely at the bottom (REPIN_PX, which also absorbs sub-pixel rounding);
 * the wider band re-pins only when the user's own latest gesture was downward,
 * i.e. they are heading back.
 */
export const REPIN_PX = 8;

/** A wheel tick with positive deltaY is the user reaching downward. */
export function wheelHeadsDown(deltaY) {
  return deltaY > 0;
}

/** A touch dragging UP the screen scrolls the content down. */
export function touchHeadsDown(startY, currentY) {
  return startY - currentY > TOUCH_SLOP_PX;
}

/** Keys that move toward the newest content. */
export function keyHeadsDown(key) {
  return key === 'ArrowDown' || key === 'PageDown' || key === 'End' || key === ' ';
}

/**
 * Should a scroll event re-engage the follow?
 * lastGesture is 'up', 'down' or null (no gesture yet).
 */
export function shouldRepin({ fromBottom, lastGesture }) {
  if (!Number.isFinite(fromBottom)) return false;
  if (fromBottom <= REPIN_PX) return true;
  return lastGesture === 'down' && fromBottom <= NEAR_BOTTOM_PX;
}

/*
 * Enter in the composer. Desktop: Enter sends, Shift+Enter is a newline.
 * Touch: Enter is a newline and only the send button sends, because a phone
 * keyboard's return key is where people break lines, and a hardware-keyboard
 * modifier is not available on glass. IME composition never sends.
 */
export function enterSends({ key, shiftKey, isComposing, touch }) {
  if (key !== 'Enter' || isComposing) return false;
  if (touch) return false;
  return !shiftKey;
}
